import type { PrimitiveDesignTokens, SemanticDesignTokens } from '@primevue/themes/lara'

export const SubBranches = {
  servicioDeAsistencia: 'SERVICIO DE ASISTENCIA',
  ambulanciaAerea: 'AMBULANCIA AEREA',
  autoVoluntario: 'AUTO VOLUNTARIO',
  motoVoluntario: 'MOTO VOLUNTARIO',
  autoexceso: 'AUTOEXCESO',
  seguroDeDesempleo: 'SEGURO DE DESEMPLEO',
  fianzasParaLicitacion: 'FIANZAS PARA LICITACION',
  prestamoHipotecario: 'PRESTAMO HIPOTECARIO',
  prestamoConsumo: 'PRESTAMO CONSUMO',
  prestamoVehiculo: 'PRESTAMO VEHICULO',
  asistenciaTc: 'ASISTENCIA TC',
  lineaDeCreditoFlexible: 'LINEA DE CREDITO FLEXIBLE',
  cuentaDeAhorro: 'CUENTA DE AHORRO',
  prestamoIncendio: 'PRESTAMO INCENDIO',
  todoRiesgoConstrucciones: 'TODO RIESGO CONSTRUCCIONES',
  averiaDeMaquinarias: 'AVERIA DE MAQUINARIAS',
  todoRiesgoEquipoElectronico: 'TODO RIESGO EQUIPO ELECTRONICO',
  incendioVoluntario: 'INCENDIO VOLUNTARIO',
  garantiaDeElectrodomesticos: 'GARANTIA DE ELECTRODOMESTICOS',
  mascotasIndividual: 'MASCOTAS INDIVIDUAL',
  indemnizacionPorHospitalizacion: 'INDEMNIZACION POR HOSPITALIZACION',
  accidentesPersonales: 'ACCIDENTES PERSONALES',
  asistenciaFuneraria: 'ASISTENCIA FUNERARIA',
  enfermedadesGraves: 'ENFERMEDADES GRAVES',
  saludMujer: 'SALUD MUJER',
  vidaIndividual: 'VIDA INDIVIDUAL',
  vidaConAhorro: 'VIDA CON AHORRO',
  indemnizacionPorCancer: 'INDEMNIZACION POR CANCER',
  responsabilidadCivil: 'RESPONSABILIDAD CIVIL',
  saludIndividual: 'SALUD INDIVIDUAL',
  saludColectivo: 'SALUD COLECTIVO',
  saludDental: 'SALUD DENTAL',
  saludVisual: 'SALUD VISUAL',
  seguroDeViajes: 'SEGURO DE VIAJES',
}

export type SubBranches = keyof typeof SubBranches

export type Branch =
  | {
      branchName: SiteApiSchema<'RamoCobertura'>
      subBranches: {
        id: number
        name: SubBranches[keyof SubBranches]
      }[]
    }
  | undefined

export const ArgentoInsurancePaymentPeriodicities = {
  unique: {
    text: 'Pago único',
    value: 'unique',
    id: 0,
  },
  monthly: {
    text: 'Mensual',
    value: 'monthly',
    id: 1,
  },
  bimonthly: {
    text: 'Bimestral',
    value: 'bimonthly',
    id: 2,
  },
  quarterly: {
    text: 'Trimestral',
    value: 'quarterly',
    id: 3,
  },
  fourMonthly: {
    text: 'Cuatrimestral',
    value: 'fourMonthly',
    id: 4,
  },
  semiannual: {
    text: 'Semestral',
    value: 'semiannual',
    id: 6,
  },
  yearly: {
    text: 'Anual',
    value: 'yearly',
    id: 12,
  },
  manual: {
    text: 'Manual',
    value: 'manual',
    id: 99,
  },
} as const

export interface BrokerData {
  context: BrokerContext
  preset?: Partial<{
    globalBorderRadius: string
    primaryColorHex: string
    surfaceColorHex: string
    accentColorHex: string
    buttonsColorHex: string
    cardHeaderColorHex: string
    tokens?: {
      primitive?: Partial<PrimitiveDesignTokens>
      semantic?: Partial<SemanticDesignTokens>
    }
  }>
  stylesRoute?: string
  termsAndConditions?: {
    text: string
    extenalLinks?: {
      text: string
      url: string
    }[]
  }
  contact?: ContactInfo[]
  notifications?: string[]
  externalLinks?: Partial<Record<string, string>>
  disclaimers?: Disclimer[]
  brokerWebsite?: string
}

export interface BrokerContext {
  id: number
  brokerName: string
  googleTagManagerId: string
  logoUrl: string
  faviconUrl: string
  copyright: string
}

export interface ProductPlanSumary {
  title?: string
  description:
    | {
        label: string
        detail?: string
        type?: 'NOTE' | 'ITEM'
      }[]
    | null
}

export interface AssociatedCompany {
  argentoInsuranceId: number
  code: string
  logoUrl: string
  logoWhiteUrl: string
  name: string
  allowPortIn: boolean
  score: number
  avaliablePlans: avaliablePlans
  quoteWithVersion: boolean
}

export type avaliablePlans = Record<
  Lowercase<SiteApiSchema<'RamoCobertura'>>,
  Record<string, boolean> & {
    default?: string
  }
>

export interface ConfiguredCoverageBenefit {
  identifier: string
  iconUrl?: string
  tag: string | null
  text: string
  visible: ('coverages' | 'benefits' | 'details' | 'highlight')[]
  maxValue?: number
  measure?: CoverageBenefitValueMeasure
}

export interface AssociatedCompanyCoverageBenefit {
  associatedCompanyCode: string
  aplicableCoverageBenefits: CoverageBenefit[]
}

export interface CoverageBenefit {
  identifier: string
  values: CoverageBenefitValue[]
}

export interface CoverageBenefitValue {
  amount?: number
  measure?: CoverageBenefitValueMeasure
  planCode: string
}

export type CoverageBenefitValueMeasure = 'MM' | '%' | 'days' | '$' | 'K' | 'US$' | 'EUR€'

export interface CoveragePlansConfig {
  groups: {
    code: string
    name: string
    description: string
    iconUrl: string
  }[]
  plans: CoveragePlan[]
  defaultGroup: string
  defaultPlan: string
}

type PlanParameters<
  Branch extends SiteApiSchema<'RamoCobertura'>,
  Config extends object = object,
> = {
  branch: Branch
  config: ({
    coverageTypeOrPlanCodes: string[]
    aplicablePaymentConditions: string[] | null
  } & Config)[]
}

export type PlanParametersConfig =
  | PlanParameters<Extract<SiteApiSchema<'RamoCobertura'>, 'Auto'>, { minVehicleYear: number }>
  | PlanParameters<Exclude<SiteApiSchema<'RamoCobertura'>, 'Auto'>>

export interface CoveragePlan {
  argentoInsuranceid: string
  argentoInsuranceCode?: string
  code: string
  name: string
  hint: string
  isBasic?: boolean
  group?: string
}

export type ItemAttribute<T = unknown> = Partial<Record<'icon' | 'text', string> & { value: T }>

export interface FeedbackSubmision {
  returnUrl?: string
  SUCCESS: ItemAttribute<Partial<Record<'icon' | 'text', string>>>[]
  FAILED: {
    FINANCE: string
    PROCESS_ERROR: string
  }
}

export type Disclimer = {
  identifier: string
  body: string
}

export interface ContactInfo {
  iconClass: string
  name: string
  contact: string
  href: string
  type: 'c-to-b' | 'social-media' | 'physical'
}
